import { useState, useEffect, FormEvent, useRef, useMemo, TouchEvent } from 'react';

interface JobSlot {
  id: string;
  date: Date;
  timeStart: string;
  timeEnd: string;
  role: string;
  hourlyRate: number;
  spotsLeft: number;
  tags: string[];
  shiftType: 'morning' | 'afternoon' | 'fullday';
  location: string;
  address: string;
  items: string[];
  notes: string;
}

// 日本の祝日判定関数
const isJapaneseHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = date.getDay();

  // 固定祝日
  const fixedHolidays: Record<string, number[]> = {
    '1': [1], // 元日
    '2': [11, 23], // 建国記念の日、天皇誕生日
    '4': [29], // 昭和の日
    '5': [3, 4, 5], // 憲法記念日、みどりの日、こどもの日
    '8': [11], // 山の日
    '11': [3, 23], // 文化の日、勤労感謝の日
  };

  if (fixedHolidays[month.toString()]?.includes(day)) {
    return true;
  }

  // ハッピーマンデー（第n月曜日）
  const nthMonday = Math.ceil(day / 7);
  if (dayOfWeek === 1) {
    if ((month === 1 && nthMonday === 2) || // 成人の日
        (month === 7 && nthMonday === 3) || // 海の日
        (month === 9 && nthMonday === 3) || // 敬老の日
        (month === 10 && nthMonday === 2)) { // スポーツの日
      return true;
    }
  }

  // 春分の日・秋分の日（簡易計算）
  if (month === 3 && day >= 19 && day <= 21) return true;
  if (month === 9 && day >= 22 && day <= 24) return true;

  return false;
};

const generateJobSlots = (): JobSlot[] => {
  const today = new Date();
  const slots: JobSlot[] = [];

  const jobTemplates = [
    {
      role: '保育士業務',
      time: { start: '8:00', end: '13:00' },
      rate: 1300,
      tags: ['資格必須'],
      shiftType: 'morning' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['エプロン', '上履き', '筆記用具'],
      notes: '動きやすい服装でお越しください',
    },
    {
      role: '保育士業務',
      time: { start: '11:00', end: '16:00' },
      rate: 1300,
      tags: ['資格必須'],
      shiftType: 'afternoon' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['エプロン', '上履き', '筆記用具'],
      notes: '午睡対応あり',
    },
    {
      role: '保育士業務',
      time: { start: '8:30', end: '16:30' },
      rate: 1300,
      tags: ['資格必須'],
      shiftType: 'fullday' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['エプロン', '上履き', '筆記用具', '昼食'],
      notes: '休憩60分あり',
    },
    {
      role: '看護師',
      time: { start: '8:00', end: '13:00' },
      rate: 1500,
      tags: ['資格必須'],
      shiftType: 'morning' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['ナースシューズ', '筆記用具'],
      notes: '健康チェック・投薬管理',
    },
    {
      role: '看護師',
      time: { start: '11:00', end: '16:00' },
      rate: 1500,
      tags: ['資格必須'],
      shiftType: 'afternoon' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['ナースシューズ', '筆記用具'],
      notes: '午睡時の見守りあり',
    },
    {
      role: '看護師',
      time: { start: '8:30', end: '16:30' },
      rate: 1500,
      tags: ['資格必須'],
      shiftType: 'fullday' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['ナースシューズ', '筆記用具', '昼食'],
      notes: '休憩60分あり',
    },
    {
      role: '調理補助',
      time: { start: '8:00', end: '13:00' },
      rate: 1100,
      tags: ['未経験OK'],
      shiftType: 'morning' as const,
      location: 'きらり保育園箕面園',
      address: '大阪府箕面市小野原東3丁目9-29 ラクシュリ小野原2F',
      items: ['エプロン', '三角巾', '上履き'],
      notes: '調理経験不問・盛り付け中心',
    },
  ];

  const dailyPatterns = [
    { morning: [0, 0, 1], afternoon: [1, 1], fullday: [1, 1] },
    { morning: [1, 2, 1], afternoon: [1, 1], fullday: [2, 1] },
    { morning: [1, 1, 2], afternoon: [0, 0], fullday: [0, 0] },
    { morning: [0, 0, 0], afternoon: [0, 0], fullday: [0, 0] },
    { morning: [0, 0, 0], afternoon: [2, 1], fullday: [0, 0] },
    { morning: [0, 0, 0], afternoon: [0, 0], fullday: [1, 2] },
    { morning: [2, 1, 1], afternoon: [1, 2], fullday: [1, 1] },
    { morning: [2, 0, 0], afternoon: [1, 0], fullday: [1, 0] },
    { morning: [0, 1, 1], afternoon: [1, 0], fullday: [0, 1] },
    { morning: [0, 0, 0], afternoon: [2, 2], fullday: [1, 1] },
    { morning: [1, 1, 1], afternoon: [1, 1], fullday: [1, 1] },
    { morning: [0, 1, 1], afternoon: [1, 0], fullday: [0, 1] },
    { morning: [0, 0, 0], afternoon: [0, 0], fullday: [0, 0] },
    { morning: [2, 1, 2], afternoon: [1, 1], fullday: [2, 1] },
  ];

  for (let dayOffset = 0; dayOffset < 60; dayOffset++) {
    const date = new Date(today);
    date.setDate(today.getDate() + dayOffset);
    
    // 日曜日または祝日の場合は募集なし
    if (date.getDay() === 0 || isJapaneseHoliday(date)) {
      continue;
    }
    
    const pattern = dailyPatterns[dayOffset % dailyPatterns.length];

    if (pattern.morning[0] !== undefined) {
      slots.push({
        id: `${dayOffset}-morning-hoikushi`,
        date: new Date(date),
        timeStart: jobTemplates[0].time.start,
        timeEnd: jobTemplates[0].time.end,
        role: jobTemplates[0].role,
        hourlyRate: jobTemplates[0].rate,
        spotsLeft: pattern.morning[0],
        tags: jobTemplates[0].tags,
        shiftType: 'morning',
        location: jobTemplates[0].location,
        address: jobTemplates[0].address,
        items: jobTemplates[0].items,
        notes: jobTemplates[0].notes,
      });
    }

    if (pattern.morning[1] !== undefined) {
      slots.push({
        id: `${dayOffset}-morning-kangoshi`,
        date: new Date(date),
        timeStart: jobTemplates[3].time.start,
        timeEnd: jobTemplates[3].time.end,
        role: jobTemplates[3].role,
        hourlyRate: jobTemplates[3].rate,
        spotsLeft: pattern.morning[1],
        tags: jobTemplates[3].tags,
        shiftType: 'morning',
        location: jobTemplates[3].location,
        address: jobTemplates[3].address,
        items: jobTemplates[3].items,
        notes: jobTemplates[3].notes,
      });
    }

    if (pattern.morning[2] !== undefined) {
      slots.push({
        id: `${dayOffset}-morning-chouri`,
        date: new Date(date),
        timeStart: jobTemplates[6].time.start,
        timeEnd: jobTemplates[6].time.end,
        role: jobTemplates[6].role,
        hourlyRate: jobTemplates[6].rate,
        spotsLeft: pattern.morning[2],
        tags: jobTemplates[6].tags,
        shiftType: 'morning',
        location: jobTemplates[6].location,
        address: jobTemplates[6].address,
        items: jobTemplates[6].items,
        notes: jobTemplates[6].notes,
      });
    }

    if (pattern.afternoon[0] !== undefined) {
      slots.push({
        id: `${dayOffset}-afternoon-hoikushi`,
        date: new Date(date),
        timeStart: jobTemplates[1].time.start,
        timeEnd: jobTemplates[1].time.end,
        role: jobTemplates[1].role,
        hourlyRate: jobTemplates[1].rate,
        spotsLeft: pattern.afternoon[0],
        tags: jobTemplates[1].tags,
        shiftType: 'afternoon',
        location: jobTemplates[1].location,
        address: jobTemplates[1].address,
        items: jobTemplates[1].items,
        notes: jobTemplates[1].notes,
      });
    }

    if (pattern.afternoon[1] !== undefined) {
      slots.push({
        id: `${dayOffset}-afternoon-kangoshi`,
        date: new Date(date),
        timeStart: jobTemplates[4].time.start,
        timeEnd: jobTemplates[4].time.end,
        role: jobTemplates[4].role,
        hourlyRate: jobTemplates[4].rate,
        spotsLeft: pattern.afternoon[1],
        tags: jobTemplates[4].tags,
        shiftType: 'afternoon',
        location: jobTemplates[4].location,
        address: jobTemplates[4].address,
        items: jobTemplates[4].items,
        notes: jobTemplates[4].notes,
      });
    }

    if (pattern.fullday[0] !== undefined) {
      slots.push({
        id: `${dayOffset}-fullday-hoikushi`,
        date: new Date(date),
        timeStart: jobTemplates[2].time.start,
        timeEnd: jobTemplates[2].time.end,
        role: jobTemplates[2].role,
        hourlyRate: jobTemplates[2].rate,
        spotsLeft: pattern.fullday[0],
        tags: jobTemplates[2].tags,
        shiftType: 'fullday',
        location: jobTemplates[2].location,
        address: jobTemplates[2].address,
        items: jobTemplates[2].items,
        notes: jobTemplates[2].notes,
      });
    }

    if (pattern.fullday[1] !== undefined) {
      slots.push({
        id: `${dayOffset}-fullday-kangoshi`,
        date: new Date(date),
        timeStart: jobTemplates[5].time.start,
        timeEnd: jobTemplates[5].time.end,
        role: jobTemplates[5].role,
        hourlyRate: jobTemplates[5].rate,
        spotsLeft: pattern.fullday[1],
        tags: jobTemplates[5].tags,
        shiftType: 'fullday',
        location: jobTemplates[5].location,
        address: jobTemplates[5].address,
        items: jobTemplates[5].items,
        notes: jobTemplates[5].notes,
      });
    }
  }

  return slots;
};

const jobSlots = generateJobSlots();

export default function SpotWorkCalendar() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobSlot | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const today = new Date();

  // スワイプの最小距離（ピクセル）
  const minSwipeDistance = 50;

  // 画面サイズに応じて表示日数を変更
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const daysToShow = isMobile ? 7 : 14;

  const getDaysArray = () => {
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + pageOffset + i);
      days.push(date);
    }
    return days;
  };

  const days = getDaysArray();

  const formatDate = (date: Date) => {
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    return {
      day: date.getDate(),
      dayName: dayNames[date.getDay()],
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isSunday: date.getDay() === 0,
      isSaturday: date.getDay() === 6,
    };
  };

  const getJobsForDate = (date: Date) => {
    return jobSlots.filter((slot) => slot.date.toDateString() === date.toDateString());
  };

  const getAvailableSpotsForDate = (date: Date) => {
    const jobs = getJobsForDate(date);
    return jobs.reduce((total, job) => total + job.spotsLeft, 0);
  };

  const upcomingJobsGrouped = useMemo(() => {
    const sortedJobs = [...jobSlots].sort((a, b) => {
      const dateCompare = a.date.getTime() - b.date.getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.timeStart.localeCompare(b.timeStart);
    });

    const uniqueDates = [
      ...new Set(sortedJobs.map((job) => job.date.toDateString())),
    ].slice(0, 1);
    const recentJobs = sortedJobs.filter((job) =>
      uniqueDates.includes(job.date.toDateString())
    );

    return {
      morning: recentJobs.filter((job) => job.shiftType === 'morning'),
      afternoon: recentJobs.filter((job) => job.shiftType === 'afternoon'),
      fullday: recentJobs.filter((job) => job.shiftType === 'fullday'),
    };
  }, []);

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString();
  };

  const handleApply = (job: JobSlot) => {
    setSelectedJob(job);
    setIsModalOpen(true);
    setIsSubmitted(false);
    setUploadedFile(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
    setIsSubmitted(false);
    setUploadedFile(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('JPG、PNG、GIF、PDFファイルのみアップロード可能です');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('ファイルサイズは5MB以下にしてください');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    if (uploadedFile) {
      formData.set('certificate_file', uploadedFile.name);
    } else {
      formData.set('certificate_file', 'Uncollectable');
    }

    try {
      await fetch('https://readdy.ai/api/form/d67953lbiueuoqm1r0rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          Array.from(formData.entries()).reduce<Record<string, string>>(
            (acc, [key, value]) => {
              acc[key] = typeof value === 'string' ? value : String(value);
              return acc;
            },
            {}
          )
        ).toString(),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupedJobs = useMemo(() => {
    if (!selectedDate) return null;

    const jobs = getJobsForDate(selectedDate);
    return {
      morning: jobs.filter((job) => job.shiftType === 'morning'),
      afternoon: jobs.filter((job) => job.shiftType === 'afternoon'),
      fullday: jobs.filter((job) => job.shiftType === 'fullday'),
    };
  }, [selectedDate]);

  const calendarMonths = useMemo(() => {
    if (days.length === 0) return '';
    const startMonth = days[0].getMonth() + 1;
    const endMonth = days[days.length - 1].getMonth() + 1;
    const startYear = days[0].getFullYear();
    const endYear = days[days.length - 1].getFullYear();

    if (startMonth === endMonth && startYear === endYear) {
      return `${startYear}年${startMonth}月`;
    }
    if (startYear === endYear) {
      return `${startYear}年${startMonth}月〜${endMonth}月`;
    }
    return `${startYear}年${startMonth}月〜${endYear}年${endMonth}月`;
  }, [days]);

  const canGoPrev = pageOffset > 0;
  const canGoNext = pageOffset < 46;

  const handlePrevPage = () => {
    if (canGoPrev && !isAnimating) {
      setSlideDirection('right');
      setIsAnimating(true);
      setTimeout(() => {
        setPageOffset((prev) => Math.max(0, prev - daysToShow));
        setSelectedDate(null);
        setSlideDirection(null);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleNextPage = () => {
    if (canGoNext && !isAnimating) {
      setSlideDirection('left');
      setIsAnimating(true);
      setTimeout(() => {
        setPageOffset((prev) => Math.min(46, prev + daysToShow));
        setSelectedDate(null);
        setSlideDirection(null);
        setIsAnimating(false);
      }, 200);
    }
  };

  // スワイプ開始
  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // スワイプ中
  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // スワイプ終了
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && canGoNext) {
      handleNextPage();
    } else if (isRightSwipe && canGoPrev) {
      handlePrevPage();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const JobCard = ({
    job,
    colorScheme,
  }: {
    job: JobSlot;
    colorScheme: 'amber' | 'sky' | 'emerald';
  }) => {
    const colors = {
      amber: {
        bg: 'bg-white',
        border: 'border-amber-100',
        icon: 'bg-amber-50',
        iconText: 'text-amber-600',
      },
      sky: {
        bg: 'bg-white',
        border: 'border-sky-100',
        icon: 'bg-sky-50',
        iconText: 'text-sky-600',
      },
      emerald: {
        bg: 'bg-white',
        border: 'border-emerald-100',
        icon: 'bg-emerald-50',
        iconText: 'text-emerald-600',
      },
    };
    const c = colors[colorScheme];
    const isFilled = job.spotsLeft === 0;

    return (
      <div className={`${c.bg} rounded-lg p-3 border ${c.border} ${isFilled ? 'opacity-50 grayscale' : 'hover:shadow-md'} transition-all relative overflow-hidden`}>
        {isFilled && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-gray-800/80 text-white px-4 py-2 rounded-lg font-bold text-sm transform -rotate-12 shadow-lg">
              <i className="ri-close-circle-line mr-1"></i>
              募集終了
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 ${c.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <i
              className={`
                text-base ${c.iconText}
                ${job.role === '保育士業務' ? 'ri-heart-line' : ''}
                ${job.role === '看護師' ? 'ri-nurse-line' : ''}
                ${job.role === '調理補助' ? 'ri-restaurant-line' : ''}
              `}
            ></i>
          </div>
          <div className="flex-1 min-w-0">
            <h5 className="font-bold text-gray-900 text-sm truncate">{job.role}</h5>
            <div className="flex items-center gap-2 text-xs">
              {isFilled ? (
                <span className="text-gray-400 font-medium line-through">募集終了</span>
              ) : (
                <span className="text-orange-600 font-bold bg-orange-50 px-2 py-0.5 rounded-full">
                  <i className="ri-user-line mr-0.5"></i>
                  あと{job.spotsLeft}名募集中
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-1.5 sm:space-y-2 text-xs">
          <div className="flex items-start gap-1.5 sm:gap-2 text-gray-600">
            <i className="ri-map-pin-line text-gray-400 mt-0.5 sm:mt-1 flex-shrink-0"></i>
            <span className="truncate max-w-[140px] sm:max-w-[180px]">{job.location}</span>
          </div>
          <div className="flex items-start gap-1.5 sm:gap-2 text-gray-600">
            <i className="ri-calendar-line text-gray-400 mt-0.5 sm:mt-1 flex-shrink-0"></i>
            <span className="truncate">
              {job.date.getMonth() + 1}/{job.date.getDate()}（{formatDate(job.date).dayName}）
            </span>
          </div>
          <div className="flex items-start gap-1.5 sm:gap-2 text-gray-600">
            <i className="ri-briefcase-line text-gray-400 mt-0.5 sm:mt-1 flex-shrink-0"></i>
            <span className="truncate">
              {job.items.slice(0, 2).join('・')}
              {job.items.length > 2 ? '...' : ''}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className={`font-bold text-sm ${isFilled ? 'text-gray-400' : 'text-teal-600'}`}>
            ¥{job.hourlyRate.toLocaleString()}
            <span className="text-xs font-normal text-gray-500">/時</span>
          </span>
          {isFilled ? (
            <span className="bg-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap">
              受付終了
            </span>
          ) : (
            <button
              onClick={() => handleApply(job)}
              className="bg-teal-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              応募
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="py-8 sm:py-12 bg-gradient-to-b from-teal-50/50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-amber-500 text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold">
                <i className="ri-building-2-line"></i>
                きらり保育園箕面園 専用
              </div>
              <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-teal-600 text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium">
                <i className="ri-calendar-check-line"></i>
                スポットワーク募集中
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
              <span className="text-teal-600">箕面園</span>で今すぐ働ける<span className="text-teal-600">お仕事カレンダー</span>
            </h2>
            <p className="text-gray-600 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              気になる日付をタップして、募集中のお仕事をチェック
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500">
            <span className="flex items-center gap-1 sm:gap-1.5">
              <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-teal-500 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center">3</span>
              募集人数
            </span>
            <span className="flex items-center gap-1 sm:gap-1.5">
              <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gray-300 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center">0</span>
              募集なし
            </span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div 
          ref={calendarRef}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 mb-4 sm:mb-6"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Month Display & Navigation */}
          <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-100">
            <button
              onClick={handlePrevPage}
              disabled={!canGoPrev || isAnimating}
              className={`flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                canGoPrev && !isAnimating
                  ? 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-left-s-line text-sm sm:text-base"></i>
              <span className="hidden sm:inline">前の{daysToShow}日間</span>
              <span className="sm:hidden">前へ</span>
            </button>

            <div className="flex items-center gap-1 sm:gap-2">
              <i className="ri-calendar-2-line text-teal-600 text-base sm:text-xl"></i>
              <span className="text-xs sm:text-lg font-bold text-gray-900">{calendarMonths}</span>
            </div>

            <button
              onClick={handleNextPage}
              disabled={!canGoNext || isAnimating}
              className={`flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                canGoNext && !isAnimating
                  ? 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">次の{daysToShow}日間</span>
              <span className="sm:hidden">次へ</span>
              <i className="ri-arrow-right-s-line text-sm sm:text-base"></i>
            </button>
          </div>

          {/* スワイプヒント（モバイルのみ） */}
          {isMobile && (
            <div className="flex items-center justify-center gap-2 mb-2 text-[10px] text-gray-400">
              <i className="ri-hand-coin-line"></i>
              <span>左右にスワイプで切り替え</span>
            </div>
          )}

          <div 
            className={`grid grid-cols-7 gap-1 sm:gap-2 md:gap-3 transition-all duration-200 ${
              slideDirection === 'left' ? 'translate-x-[-10px] opacity-50' : 
              slideDirection === 'right' ? 'translate-x-[10px] opacity-50' : ''
            }`}
          >
            {days.map((date, index) => {
              const { day, dayName, isSunday, isSaturday } = formatDate(date);
              const availableSpots = getAvailableSpotsForDate(date);
              const hasAvailableJobs = availableSpots > 0;

              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className={`
                    relative flex flex-col items-center p-1 sm:p-2 md:p-3 rounded-lg sm:rounded-xl transition-all duration-200 cursor-pointer min-h-[52px] sm:min-h-[72px]
                    ${isSelected(date)
                      ? 'bg-teal-600 text-white shadow-lg scale-105'
                      : isToday(date)
                      ? 'bg-teal-100 text-teal-800'
                      : hasAvailableJobs
                      ? 'bg-gray-50 hover:bg-gray-100'
                      : 'bg-gray-100/50 hover:bg-gray-100'}
                  `}
                >
                  <span
                    className={`
                      text-[9px] sm:text-xs font-medium mb-0.5
                      ${isSelected(date)
                        ? 'text-teal-100'
                        : isSunday
                        ? 'text-red-500'
                        : isSaturday
                        ? 'text-blue-500'
                        : 'text-gray-500'}
                    `}
                  >
                    {dayName}
                  </span>
                  <span
                    className={`
                      text-sm sm:text-lg md:text-xl font-bold leading-tight
                      ${isSelected(date) ? 'text-white' : hasAvailableJobs ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {day}
                  </span>
                  {isToday(date) && !isSelected(date) && (
                    <span className="text-[7px] sm:text-[10px] text-teal-600 font-medium leading-tight">今日</span>
                  )}
                  <span
                    className={`
                      absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-3.5 h-3.5 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full text-[8px] sm:text-xs font-bold flex items-center justify-center
                      ${isSelected(date) 
                        ? hasAvailableJobs 
                          ? 'bg-yellow-400 text-gray-900' 
                          : 'bg-gray-400 text-white'
                        : hasAvailableJobs 
                          ? 'bg-teal-500 text-white' 
                          : 'bg-gray-300 text-white'}
                    `}
                  >
                    {availableSpots}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Job List */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 animate-fadeIn">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              {selectedDate
                ? `${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日（${formatDate(
                    selectedDate
                  ).dayName}）の募集`
                : '直近のお仕事'}
            </h3>
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="text-xs sm:text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i> 選択解除
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* 午前カラム */}
            <div className="bg-amber-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2 sm:mb-3 pb-2 border-b border-amber-200">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-sun-line text-amber-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">午前</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500">8:00〜13:00</p>
                </div>
              </div>
              <div className="space-y-2">
                {(selectedDate && groupedJobs ? groupedJobs.morning : upcomingJobsGrouped.morning)
                  .length > 0 ? (
                  (selectedDate && groupedJobs ? groupedJobs.morning : upcomingJobsGrouped.morning).map(
                    (job) => <JobCard key={job.id} job={job} colorScheme="amber" />
                  )
                ) : (
                  <p className="text-xs sm:text-sm text-gray-400 text-center py-3 sm:py-4">募集なし</p>
                )}
              </div>
            </div>

            {/* 午後カラム */}
            <div className="bg-sky-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-sky-200">
              <div className="flex items-center gap-2 mb-2 sm:mb-3 pb-2 border-b border-sky-200">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <i className="ri-sun-cloudy-line text-sky-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">午後</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500">11:00〜16:00</p>
                </div>
              </div>
              <div className="space-y-2">
                {(selectedDate && groupedJobs ? groupedJobs.afternoon : upcomingJobsGrouped.afternoon)
                  .length > 0 ? (
                  (selectedDate && groupedJobs ? groupedJobs.afternoon : upcomingJobsGrouped.afternoon).map(
                    (job) => <JobCard key={job.id} job={job} colorScheme="sky" />
                  )
                ) : (
                  <p className="text-xs sm:text-sm text-gray-400 text-center py-3 sm:py-4">募集なし</p>
                )}
              </div>
            </div>

            {/* 通しカラム */}
            <div className="bg-emerald-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-emerald-200 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-2 sm:mb-3 pb-2 border-b border-emerald-200">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-emerald-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">通し</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500">8:30〜16:30</p>
                </div>
              </div>
              <div className="space-y-2">
                {(selectedDate && groupedJobs ? groupedJobs.fullday : upcomingJobsGrouped.fullday)
                  .length > 0 ? (
                  (selectedDate && groupedJobs ? groupedJobs.fullday : upcomingJobsGrouped.fullday).map(
                    (job) => <JobCard key={job.id} job={job} colorScheme="emerald" />
                  )
                ) : (
                  <p className="text-xs sm:text-sm text-gray-400 text-center py-3 sm:py-4">募集なし</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          ></div>
          <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl">
              <button
                onClick={handleCloseModal}
                className="absolute top-3 sm:top-4 right-3 sm:right-4 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg sm:text-xl"></i>
              </button>
              <h3 className="text-base sm:text-lg font-bold text-gray-900">お仕事に応募する</h3>
              <p className="text-[10px] sm:text-xs text-teal-600 font-medium">きらり保育園箕面園</p>
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-teal-50/50 border-b border-teal-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i
                    className={`
                      text-base sm:text-lg text-teal-600
                      ${selectedJob.role === '保育士業務' ? 'ri-heart-line' : ''}
                      ${selectedJob.role === '看護師' ? 'ri-nurse-line' : ''}
                      ${selectedJob.role === '調理補助' ? 'ri-restaurant-line' : ''}
                    `}
                  ></i>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm sm:text-base">{selectedJob.role}</p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {selectedJob.date.getMonth() + 1}月{selectedJob.date.getDate()}日{' '}
                    {selectedJob.timeStart}〜{selectedJob.timeEnd} ・ 時給¥
                    {selectedJob.hourlyRate.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-2 sm:p-3 space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                <div className="flex items-start gap-1.5 sm:gap-2">
                  <i className="ri-map-pin-line text-teal-600 mt-0.5"></i>
                  <div>
                    <span className="font-medium text-gray-700">勤務地：</span>
                    <span className="text-gray-600">{selectedJob.location}</span>
                    <p className="text-[10px] sm:text-xs text-gray-500">{selectedJob.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 sm:gap-2">
                  <i className="ri-briefcase-line text-teal-600 mt-0.5"></i>
                  <div>
                    <span className="font-medium text-gray-700">持ち物：</span>
                    <span className="text-gray-600">{selectedJob.items.join('、')}</span>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 sm:gap-2">
                  <i className="ri-information-line text-teal-600 mt-0.5"></i>
                  <div>
                    <span className="font-medium text-gray-700">備考：</span>
                    <span className="text-gray-600">{selectedJob.notes}</span>
                  </div>
                </div>
              </div>

              <div className="mt-2 sm:mt-3 rounded-lg overflow-hidden border border-teal-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3274.8!2d135.5!3d34.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDQ4JzAwLjAiTiAxMzXCsDMwJzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1700000000000!5m2!1sja!2sjp&q=大阪府箕面市小野原東3丁目9-29"
                  width="100%"
                  height="140"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="きらり保育園箕面園の地図"
                  className="sm:h-[180px]"
                ></iframe>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=大阪府箕面市小野原東3丁目9-29+ラクシュリ小野原2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 py-1.5 sm:py-2 bg-white text-teal-600 text-xs sm:text-sm font-medium hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Googleマップで開く
                </a>
              </div>
            </div>

            {isSubmitted ? (
              <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-check-line text-2xl sm:text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">応募が完了しました！</h4>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  きらり保育園箕面園の担当者より<br />
                  折り返しご連絡いたします。
                </p>
                <button
                  onClick={handleCloseModal}
                  className="bg-teal-600 text-white px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  閉じる
                </button>
              </div>
            ) : (
              <form
                id="spot-work-application-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="px-4 sm:px-6 py-4 sm:py-5"
              >
                <input type="hidden" name="job_role" value={selectedJob.role} />
                <input
                  type="hidden"
                  name="job_date"
                  value={`${selectedJob.date.getMonth() + 1}月${selectedJob.date.getDate()}日`}
                />
                <input
                  type="hidden"
                  name="job_time"
                  value={`${selectedJob.timeStart}〜${selectedJob.timeEnd}`}
                />
                <input
                  type="hidden"
                  name="job_hourly_rate"
                  value={`¥${selectedJob.hourlyRate.toLocaleString()}`}
                />
                <input type="hidden" name="job_location" value={selectedJob.location} />
                <input type="hidden" name="job_address" value={selectedJob.address} />

                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 花子"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="090-1234-5678"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      関連業務の経験 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3 sm:gap-4">
                      <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="experience"
                          value="経験あり"
                          required
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-xs sm:text-sm text-gray-700">経験あり</span>
                      </label>
                      <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="experience"
                          value="未経験"
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-xs sm:text-sm text-gray-700">未経験</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      資格証明書{' '}
                      <span className="text-gray-400 text-[10px] sm:text-xs font-normal">
                        （任意・JPG/PNG/PDF 5MB以下）
                      </span>
                    </label>
                    {!uploadedFile ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-200 rounded-lg p-4 sm:p-6 text-center hover:border-teal-400 hover:bg-teal-50/30 transition-all cursor-pointer"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.gif,.pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                          <i className="ri-upload-cloud-2-line text-xl sm:text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">クリックしてファイルを選択</p>
                        <p className="text-[10px] sm:text-xs text-gray-400">保育士証・看護師免許など</p>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                              {uploadedFile.type === 'application/pdf' ? (
                                <i className="ri-file-pdf-2-line text-lg sm:text-xl text-teal-600"></i>
                              ) : (
                                <i className="ri-image-line text-lg sm:text-xl text-teal-600"></i>
                              )}
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[140px] sm:max-w-[180px]">
                                {uploadedFile.name}
                              </p>
                              <p className="text-[10px] sm:text-xs text-gray-500">
                                {(uploadedFile.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm sm:text-lg"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                      メッセージ（任意）
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="自己PRや質問などがあればご記入ください"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    ></textarea>
                  </div>
                </div>

                <div className="mt-4 sm:mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white py-2.5 sm:py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </>
                    ) : (
                      <>
                        応募を送信する
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                </div>

                <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-3 sm:mt-4">
                  応募後、きらり保育園箕面園の担当者より折り返しご連絡いたします
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}
